/**
 * Simple-mode input surface (~20 inputs; spec §21 progressive disclosure).
 * Advanced mode grows in later phases. Currency inputs accept "$1,234,567",
 * "450k", "1.2m". Every change re-projects immediately.
 */
import { useState } from 'react';
import type { Plan } from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import { stateProfileFor } from '../engine/data/states';
import { useFieldTip } from './FieldTip';
import { PersonRow } from './PersonRow';
import { TIPS } from './tips';
import { fmtCompactUsd, parseCurrency } from './format';

interface Props {
  plan: Plan;
  onChange: (updated: Plan) => void;
}

export function CurrencyField({
  label, value, onCommit, hint, allowNegative, tip,
}: {
  label: string; value: number; onCommit: (n: number) => void;
  hint?: string; allowNegative?: boolean; tip?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const { handlers, bubble } = useFieldTip(tip);
  const shown = text ?? value.toLocaleString('en-US');
  return (
    <div className="field" {...handlers}>
      <label>
        {label}
        <input
          inputMode="decimal"
          value={shown}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text != null) {
              const n = parseCurrency(text);
              if (Number.isFinite(n) && (allowNegative || n >= 0)) onCommit(n);
              setText(null);
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </label>
      {hint && <span className="hint">{hint}</span>}
      {bubble}
    </div>
  );
}

export function TextField({
  label, value, onCommit, hint, placeholder, tip,
}: {
  label: string; value: string; onCommit: (s: string) => void;
  hint?: string; placeholder?: string; tip?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const { handlers, bubble } = useFieldTip(tip);
  return (
    <div className="field" {...handlers}>
      <label>
        {label}
        <input
          type="text"
          value={text ?? value}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text != null) {
              onCommit(text.trim());
              setText(null);
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </label>
      {hint && <span className="hint">{hint}</span>}
      {bubble}
    </div>
  );
}

export function NumberField({
  label, value, min, max, onCommit, hint, tip,
}: {
  label: string; value: number; min: number; max: number;
  onCommit: (n: number) => void; hint?: string; tip?: string;
}) {
  // Draft-text pattern: let the user type freely (including partial values
  // like "6" on the way to "65"), commit + clamp on blur or Enter.
  const [text, setText] = useState<string | null>(null);
  const { handlers, bubble } = useFieldTip(tip);
  return (
    <div className="field" {...handlers}>
      <label>
        {label}
        <input
          type="number"
          inputMode="numeric"
          value={text ?? value}
          min={min}
          max={max}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text != null) {
              const n = Number(text);
              if (Number.isFinite(n) && text.trim() !== '') {
                onCommit(Math.min(max, Math.max(min, Math.round(n))));
              }
              setText(null);
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </label>
      {hint && <span className="hint">{hint}</span>}
      {bubble}
    </div>
  );
}

export function Inputs({ plan, onChange }: Props) {
  const person = plan.household.people[0]!;
  const spouse = plan.household.people[1];
  const spouseOn = !!spouse;

  function update(mut: (p: Plan) => void) {
    const next = structuredClone(plan);
    mut(next);
    onChange(next);
  }

  /** The person a two-column row is editing, or undefined for a missing spouse. */
  const who = (w: 'you' | 'spouse') => (w === 'you' ? person : spouse);
  const idx = (w: 'you' | 'spouse') => (w === 'you' ? 0 : 1);

  /** Edit one person in place. No-ops if that person doesn't exist. */
  const editPerson = (w: 'you' | 'spouse', mut: (person: Plan['household']['people'][number]) => void) =>
    update((p) => {
      const target = p.household.people[idx(w)];
      if (target) mut(target);
    });

  function addSpouse() {
    update((p) => {
      if (p.household.people.length > 1) return;
      p.household.people.push({
        id: 'person_spouse',
        label: 'Spouse',
        birthYear: p.household.people[0]!.birthYear,
        birthMonth: 1,
        retirementAge: 65,
        lifeExpectancyAge: 92,
        socialSecurity: { estimatedMonthlyAtFRA: 0, claimingAge: 67 },
      });
      p.household.filingStatus = 'mfj';
    });
  }

  /** Remove the spouse, reassigning anything of theirs so no money vanishes. */
  function removeSpouse() {
    update((p) => {
      const gone = p.household.people[1];
      if (!gone) return;
      p.household.people.splice(1, 1);
      p.household.filingStatus = 'single';
      const survivor = p.household.people[0]!.id;
      for (const a of p.accounts) if (a.ownerId === gone.id) a.ownerId = survivor;
      p.incomes = p.incomes.filter((i) => i.ownerId !== gone.id);
      p.ltcEvents = p.ltcEvents?.filter((e) => e.personId !== gone.id);
    });
  }

  /** Salary stream belonging to one person (each person has at most one here). */
  const salaryOf = (w: 'you' | 'spouse') => {
    const owner = who(w);
    return owner
      ? plan.incomes.find(
          (i) => (i.type === 'w2' || i.type === 'self_employment') && i.ownerId === owner.id,
        )
      : undefined;
  };

  function setSalary(w: 'you' | 'spouse', amount: number) {
    update((p) => {
      const owner = p.household.people[idx(w)];
      if (!owner) return;
      const existing = p.incomes.find(
        (i) => (i.type === 'w2' || i.type === 'self_employment') && i.ownerId === owner.id,
      );
      if (existing) { existing.annualAmount = amount; return; }
      if (amount <= 0) return;
      p.incomes.push({
        id: `inc_salary_${owner.id}`,
        ownerId: owner.id,
        label: w === 'you' ? 'Salary' : `${owner.label} salary`,
        type: 'w2',
        annualAmount: amount,
        startAge: 2026 - owner.birthYear,
        endAge: owner.retirementAge,
        taxable: true,
        taxTreatment: 'ordinary',
      });
    });
  }

  /**
   * A simple-mode contribution box stands for EVERY matching account, not just
   * the first one found. Showing one account's figure under-reports anyone with
   * both a 401(k) and an IRA (or, since the wizard learned to save into bonds,
   * a brokerage and a T-bill account), and editing it silently left the others
   * untouched. Read the sum; write it back in proportion.
   */
  const contributingIn = (family: string, ownerId?: string) =>
    plan.accounts.filter(
      (a) => ACCOUNT_META[a.type].taxFamily === family
        && (a.contributions?.employee ?? 0) > 0
        && (!ownerId || a.ownerId === ownerId),
    );

  const contributionTotal = (family: string, ownerId?: string) =>
    contributingIn(family, ownerId).reduce((t, a) => t + (a.contributions?.employee ?? 0), 0);

  /** Spread a new total across the accounts already contributing, pro rata. */
  function scaleContributions(
    family: string,
    newTotal: number,
    current: number,
    ownerId?: string,
    create?: () => Plan['accounts'][number],
  ) {
    update((p) => {
      const members = p.accounts.filter(
        (a) => ACCOUNT_META[a.type].taxFamily === family
          && (a.contributions?.employee ?? 0) > 0
          && (!ownerId || a.ownerId === ownerId),
      );
      if (members.length === 0) {
        if (newTotal <= 0 || !create) return;
        const made = create();
        // Reuse an existing empty account of the family before adding one.
        const existing = p.accounts.find(
          (a) => ACCOUNT_META[a.type].taxFamily === family && (!ownerId || a.ownerId === ownerId),
        );
        const target = existing ?? made;
        if (!existing) p.accounts.push(made);
        target.contributions = { ...(target.contributions ?? {}), employee: newTotal };
        return;
      }
      if (members.length === 1) {
        members[0]!.contributions = { ...members[0]!.contributions, employee: newTotal };
        return;
      }
      const ratio = current > 0 ? newTotal / current : 0;
      for (const m of members) {
        m.contributions = {
          ...m.contributions,
          employee: Math.round((m.contributions!.employee ?? 0) * ratio),
        };
      }
    });
  }

  function setContribution(w: 'you' | 'spouse', amount: number) {
    const owner = who(w);
    if (!owner) return;
    scaleContributions('pretax', amount, contributionTotal('pretax', owner.id), owner.id, () => ({
      id: `acct_trad_401k_${plan.accounts.length + 1}`,
      ownerId: owner.id,
      type: 'trad_401k',
      label: w === 'you' ? '401(k)' : `${owner.label} 401(k)`,
      balance: 0,
    }));
  }

  // Aggregate account balances by family for the simple editor.
  /**
   * An account the user has designated as their safety bucket is deliberately
   * sized and is managed by the Strategy tab. It must never be swept up by a
   * simple-mode aggregate edit, which would silently resize their strategy.
   */
  const bucketId = plan.decisions.bucketStrategy?.accountId;
  const inFamily = (a: Plan['accounts'][number], types: string[]) =>
    types.includes(a.type) && a.id !== bucketId;
  const bucketAccount = bucketId ? plan.accounts.find((a) => a.id === bucketId) : undefined;

  const sumBy = (types: string[]) =>
    plan.accounts.filter((a) => inFamily(a, types)).reduce((t, a) => t + a.balance, 0);
  const pretaxTotal = sumBy(['trad_401k', 'trad_403b', 'trad_457b', 'trad_ira']);
  const rothTotal = sumBy(['roth_401k', 'roth_403b', 'roth_457b', 'roth_ira']);
  const aftertaxTotal = sumBy(['brokerage', 'checking_savings', 'cd_money_market', 'bonds']);

  /** Scale the accounts in a family to a new aggregate total (simple mode). */
  function scaleFamily(types: string[], newTotal: number, current: number, fallbackType: Plan['accounts'][number]['type']) {
    update((p) => {
      const members = p.accounts.filter((a) => inFamily(a, types));
      if (members.length === 0) {
        if (newTotal > 0) {
          p.accounts.push({
            id: `acct_${fallbackType}_${p.accounts.length + 1}`,
            ownerId: person.id,
            type: fallbackType,
            label: fallbackType === 'brokerage' ? 'Brokerage' : fallbackType === 'roth_ira' ? 'Roth IRA' : '401(k)',
            balance: newTotal,
            costBasis: fallbackType === 'brokerage' ? newTotal * 0.7 : undefined,
          });
        }
        return;
      }
      if (current <= 0) {
        members[0]!.balance = newTotal;
        return;
      }
      const ratio = newTotal / current;
      for (const m of members) {
        m.balance *= ratio;
        if (m.costBasis != null) m.costBasis *= ratio;
      }
    });
  }

  return (
    <div>
      <label className="simple-spouse-toggle">
        <input
          type="checkbox"
          checked={spouseOn}
          onChange={(e) => (e.target.checked ? addSpouse() : removeSpouse())}
        />
        <span>
          <strong>Planning with a spouse or partner</strong>
          <span className="hint" style={{ display: 'block' }}>
            Adds a second column so each of you gets your own ages, income, and Social
            Security — which is what drives survivor benefits and per-person withdrawal rules.
          </span>
        </span>
      </label>

      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <TextField
              label="Name (optional)"
              value={me.label === 'You' || me.label === 'Spouse' ? '' : me.label}
              placeholder={w === 'you' ? 'You' : 'Spouse'}
              onCommit={(v) => editPerson(w, (t) => { t.label = v || (w === 'you' ? 'You' : 'Spouse'); })}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <NumberField
              label="Age" min={18} max={100} tip={TIPS.age}
              value={2026 - me.birthYear}
              onCommit={(age) => editPerson(w, (t) => { t.birthYear = 2026 - age; })}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <NumberField
              label="Retirement age" min={35} max={80} tip={TIPS.retirementAge}
              value={me.retirementAge}
              onCommit={(v) => editPerson(w, (t) => { t.retirementAge = v; })}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <CurrencyField
              label="Annual salary" tip={TIPS.salary}
              value={salaryOf(w)?.annualAmount ?? 0}
              onCommit={(v) => setSalary(w, v)}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <CurrencyField
              label="Annual 401(k)/IRA contribution" tip={TIPS.contribution401k}
              hint={w === 'you' ? 'While working. 2026 limit: $24,500 (+$8,000 at 50)' : undefined}
              value={Math.round(contributionTotal('pretax', me.id))}
              onCommit={(v) => setContribution(w, v)}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <CurrencyField
              label="Est. Social Security at full retirement age ($/mo)" tip={TIPS.ssMonthly}
              hint={w === 'you' ? 'From your SSA.gov statement' : undefined}
              value={me.socialSecurity.estimatedMonthlyAtFRA ?? 0}
              onCommit={(v) => editPerson(w, (t) => { t.socialSecurity.estimatedMonthlyAtFRA = v; })}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <NumberField
              label="Social Security claiming age" min={62} max={70} tip={TIPS.claimingAge}
              hint={w === 'you' ? '62 = reduced · 67 = full · 70 = maximum' : undefined}
              value={me.socialSecurity.claimingAge}
              onCommit={(v) => editPerson(w, (t) => { t.socialSecurity.claimingAge = v; })}
            />
          ) : null;
        }}
      </PersonRow>
      <PersonRow spouseOn={spouseOn} youName={person.label} spouseName={spouse?.label}>
        {(w) => {
          const me = who(w);
          return me ? (
            <NumberField
              label="Plan to age" min={70} max={105} tip={TIPS.planToAge}
              value={me.lifeExpectancyAge}
              onCommit={(v) => editPerson(w, (t) => { t.lifeExpectancyAge = v; })}
            />
          ) : null;
        }}
      </PersonRow>

      <div className="section-title" style={{ fontSize: 'var(--fs-body)', margin: 'var(--space-5) 0 var(--space-3)' }}>
        Household
      </div>
      <div className="inputs-grid">
        <CurrencyField
          label="Annual spending" tip={TIPS.annualSpending}
          value={plan.budget.annualAmount ?? 0}
          hint={
            plan.budget.mode === 'simple'
              ? "Today's dollars, before taxes"
              : `Budget is in ${plan.budget.mode} mode — edit it under Advanced → Budget`
          }
          onCommit={(v) => update((p) => { p.budget.annualAmount = v; })}
        />
        <CurrencyField
          label="Pre-tax accounts (401k, IRA…)"
          value={Math.round(pretaxTotal)}
          hint={spouseOn ? 'Combined. Split them per person under Advanced → Accounts.' : undefined}
          onCommit={(v) => scaleFamily(['trad_401k', 'trad_403b', 'trad_457b', 'trad_ira'], v, pretaxTotal, 'trad_401k')}
        />
        <CurrencyField
          label="Roth accounts"
          value={Math.round(rothTotal)}
          onCommit={(v) => scaleFamily(['roth_401k', 'roth_403b', 'roth_457b', 'roth_ira'], v, rothTotal, 'roth_ira')}
        />
        <CurrencyField
          label="Taxable + cash accounts"
          value={Math.round(aftertaxTotal)}
          hint={bucketAccount
            ? `Excludes your ${fmtCompactUsd(bucketAccount.balance)} safety bucket, which is sized under Strategy`
            : undefined}
          onCommit={(v) => scaleFamily(['brokerage', 'checking_savings', 'cd_money_market', 'bonds'], v, aftertaxTotal, 'brokerage')}
        />
        <CurrencyField
          label="Annual taxable-account savings" tip={TIPS.taxableSavings}
          value={Math.round(contributionTotal('aftertax'))}
          hint={
            contributingIn('aftertax').length > 1
              ? `Total across ${contributingIn('aftertax').length} accounts — split them under Advanced → Accounts`
              : 'Brokerage/savings while working — no federal limit'
          }
          onCommit={(v) => scaleContributions('aftertax', v, contributionTotal('aftertax'), undefined, () => ({
            id: `acct_brokerage_${plan.accounts.length + 1}`,
            ownerId: person.id,
            type: 'brokerage',
            label: 'Brokerage',
            balance: 0,
            costBasis: 0,
          }))}
        />
        <div className="field">
          <label>
            Filing status
            <select
              value={plan.household.filingStatus}
              onChange={(e) => update((p) => { p.household.filingStatus = e.target.value as Plan['household']['filingStatus']; })}
            >
              <option value="single">Single</option>
              <option value="mfj">Married filing jointly</option>
              <option value="hoh">Head of household</option>
              <option value="qw">Qualifying surviving spouse</option>
            </select>
          </label>
        </div>
        <div className="field">
          <label>
            State
            <select
              value={plan.household.stateOfResidence}
              onChange={(e) => update((p) => { p.household.stateOfResidence = e.target.value; })}
            >
              {['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <span className="hint">
            {(() => {
              const p = stateProfileFor(plan.household.stateOfResidence);
              const label =
                p.fidelity === 'none' ? 'exact — no income tax'
                : p.fidelity === 'modeled' ? 'modeled in detail'
                : 'labeled approximation';
              return `${p.name}: ${label} (verified ${p.lastVerified}). ${p.notes}`;
            })()}
          </span>
        </div>
      </div>
    </div>
  );
}
